import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { apiAdmin, resolveUploadUrl } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import InvitationUploader from "@/components/InvitationUploader";
import {
  Save,
  Plus,
  Trash2,
  Megaphone,
  CreditCard,
  Image as ImageIcon,
  Globe,
  User as UserIcon,
  Phone,
  Building2,
  Mail,
  MessageSquare,
  RefreshCw,
} from "lucide-react";

const STATUS_META = {
  pending:   { label: "Bekliyor",   color: "bg-[#D97706]/12 text-[#D97706] border-[#D97706]/30" },
  contacted: { label: "İletişimde", color: "bg-[#1F5F8B]/12 text-[#1F5F8B] border-[#1F5F8B]/30" },
  approved:  { label: "Onaylı",     color: "bg-[#4A5D23]/12 text-[#4A5D23] border-[#4A5D23]/30" },
  rejected:  { label: "Reddedildi", color: "bg-[#7F1D1D]/12 text-[#7F1D1D] border-[#7F1D1D]/30" },
};

const AdManagement = () => {
  const { settings, refresh } = useSettings();
  const [state, setState] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);

  useEffect(() => { setState(settings); }, [settings]);

  const fetchApps = useCallback(async () => {
    setLoadingApps(true);
    try {
      const { data } = await apiAdmin.get("/ad-applications");
      setApps(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Başvurular yüklenemedi");
    } finally {
      setLoadingApps(false);
    }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const setPackage = (idx, key, val) => {
    const list = [...(state.ad_packages || [])];
    list[idx] = { ...list[idx], [key]: val };
    set("ad_packages", list);
  };
  const addPackage = () => {
    const list = [...(state.ad_packages || [])];
    list.push({ days: 30, price: 1000, label: "Yeni Paket" });
    set("ad_packages", list);
  };
  const removePackage = (idx) => {
    const list = [...(state.ad_packages || [])];
    list.splice(idx, 1);
    set("ad_packages", list);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ad_enabled: !!state.ad_enabled,
        ad_section_title: state.ad_section_title || "",
        ad_section_description: state.ad_section_description || "",
        ad_packages: (state.ad_packages || []).map((p) => ({
          days: Number(p.days) || 0,
          price: Number(p.price) || 0,
          label: String(p.label || "").trim(),
        })),
        ad_payment_bank: state.ad_payment_bank || "",
        ad_payment_holder: state.ad_payment_holder || "",
        ad_payment_iban: state.ad_payment_iban || "",
        ad_payment_note: state.ad_payment_note || "",
        ad_active_title: state.ad_active_title || "",
        ad_active_description: state.ad_active_description || "",
        ad_active_image_url: state.ad_active_image_url || null,
        ad_active_link: state.ad_active_link || "",
      };
      await apiAdmin.put("/settings", payload);
      await refresh();
      toast.success("Reklam ayarları kaydedildi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (app, status) => {
    try {
      await apiAdmin.patch(`/ad-applications/${app.id}/status`, { status });
      toast.success("Durum güncellendi");
      fetchApps();
    } catch {
      toast.error("Güncellenemedi");
    }
  };

  const deleteApp = async (app) => {
    if (!window.confirm("Bu başvuruyu silmek istiyor musunuz?")) return;
    try {
      await apiAdmin.delete(`/ad-applications/${app.id}`);
      toast.success("Silindi");
      fetchApps();
    } catch {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="space-y-8" data-testid="ad-management">
      {/* Section header + on/off */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-[#8A252C]/10 flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-[#8A252C]" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Reklam</div>
            <h3 className="font-display text-xl font-bold text-[#1F2937]">Reklam Alanı Ayarları</h3>
            <p className="text-sm text-[#4B5563] mt-0.5">
              Üye sayfasında görünen reklam bölümünü aç/kapat, başlık ve açıklamayı düzenle.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#4B5563]">Aktif</span>
            <Switch
              checked={!!state.ad_enabled}
              onCheckedChange={(v) => set("ad_enabled", v)}
              data-testid="ad-enabled-toggle"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Başlık</Label>
            <Input value={state.ad_section_title || ""} onChange={(e) => set("ad_section_title", e.target.value)}
              placeholder="Reklam Alanı" data-testid="ad-section-title-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Açıklama</Label>
            <Textarea rows={2} value={state.ad_section_description || ""} onChange={(e) => set("ad_section_description", e.target.value)}
              placeholder="Site giderlerine katkı..." data-testid="ad-section-description-input"
              className="mt-1 bg-stone-50 border-stone-300 resize-none" />
          </div>
        </div>
      </div>

      {/* Packages */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#8A252C]/10 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-[#8A252C]" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Fiyatlandırma</div>
              <h3 className="font-display text-xl font-bold text-[#1F2937]">Reklam Paketleri</h3>
              <p className="text-sm text-[#4B5563] mt-0.5">Süre, tutar ve etiket ekleyin.</p>
            </div>
          </div>
          <Button onClick={addPackage} data-testid="ad-package-add-btn" className="bg-[#8A252C] hover:bg-[#6f1d24] text-white gap-2 rounded-xl h-10">
            <Plus className="w-4 h-4" /> Yeni Paket
          </Button>
        </div>

        <div className="space-y-2">
          {(state.ad_packages || []).length === 0 && (
            <p className="text-sm text-[#4B5563] text-center py-4">Henüz paket yok. "Yeni Paket" ile ekleyin.</p>
          )}
          {(state.ad_packages || []).map((p, i) => (
            <div key={i} data-testid={`ad-package-row-${i}`} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center border border-stone-200 rounded-xl p-2">
              <Input value={p.label || ""} onChange={(e) => setPackage(i, "label", e.target.value)}
                placeholder="Etiket (Örn: 1 Ay)" data-testid={`ad-package-label-${i}`}
                className="sm:col-span-5 bg-stone-50 border-stone-300 h-11" />
              <div className="sm:col-span-3 flex items-center gap-1">
                <Input type="number" value={p.days ?? 0} onChange={(e) => setPackage(i, "days", e.target.value)}
                  data-testid={`ad-package-days-${i}`}
                  className="bg-stone-50 border-stone-300 h-11" />
                <span className="text-xs text-[#4B5563]">gün</span>
              </div>
              <div className="sm:col-span-3 flex items-center gap-1">
                <Input type="number" value={p.price ?? 0} onChange={(e) => setPackage(i, "price", e.target.value)}
                  data-testid={`ad-package-price-${i}`}
                  className="bg-stone-50 border-stone-300 h-11" />
                <span className="text-xs text-[#4B5563]">₺</span>
              </div>
              <Button variant="ghost" onClick={() => removePackage(i)}
                data-testid={`ad-package-remove-${i}`}
                className="sm:col-span-1 text-[#7F1D1D] hover:bg-[#7F1D1D]/5 h-11 w-full sm:w-11 p-0" title="Sil">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Payment info */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-[#8A252C]/10 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-[#8A252C]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Ödeme</div>
            <h3 className="font-display text-xl font-bold text-[#1F2937]">Hesap Bilgileri</h3>
            <p className="text-sm text-[#4B5563] mt-0.5">Üyelere gösterilecek havale/EFT bilgileri.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Banka</Label>
            <Input value={state.ad_payment_bank || ""} onChange={(e) => set("ad_payment_bank", e.target.value)}
              placeholder="Örn: Ziraat Bankası" data-testid="ad-payment-bank-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Hesap Sahibi</Label>
            <Input value={state.ad_payment_holder || ""} onChange={(e) => set("ad_payment_holder", e.target.value)}
              placeholder="Ad Soyad / Dernek" data-testid="ad-payment-holder-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">IBAN</Label>
            <Input value={state.ad_payment_iban || ""} onChange={(e) => set("ad_payment_iban", e.target.value)}
              placeholder="TR..." data-testid="ad-payment-iban-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11 font-mono tracking-wider" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Not</Label>
            <Input value={state.ad_payment_note || ""} onChange={(e) => set("ad_payment_note", e.target.value)}
              placeholder="Havale açıklamasına firma adınızı yazın." data-testid="ad-payment-note-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
        </div>
      </div>

      {/* Active ad */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-[#8A252C]/10 flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-[#8A252C]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Yayında</div>
            <h3 className="font-display text-xl font-bold text-[#1F2937]">Aktif Reklam</h3>
            <p className="text-sm text-[#4B5563] mt-0.5">
              Görsel, başlık, açıklama ve bağlantı doldurulursa üye sayfasında yayınlanır.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Görsel</Label>
            <div className="mt-1">
              <InvitationUploader
                value={state.ad_active_image_url || ""}
                onChange={(url) => set("ad_active_image_url", url)}
                compact
                title="Reklam görselini seçin"
                hint="Yatay banner veya logo · en fazla 10 MB"
                doneLabel="Reklam görseli yüklendi"
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Başlık</Label>
            <Input value={state.ad_active_title || ""} onChange={(e) => set("ad_active_title", e.target.value)}
              placeholder="Reklam başlığı" data-testid="ad-active-title-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Bağlantı</Label>
            <Input value={state.ad_active_link || ""} onChange={(e) => set("ad_active_link", e.target.value)}
              placeholder="https://..." data-testid="ad-active-link-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Açıklama</Label>
            <Textarea rows={2} value={state.ad_active_description || ""} onChange={(e) => set("ad_active_description", e.target.value)}
              data-testid="ad-active-description-input"
              className="mt-1 bg-stone-50 border-stone-300 resize-none" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="ad-save-btn"
          className="bg-[#8A252C] hover:bg-[#6f1d24] text-white gap-2 rounded-xl h-12 px-6">
          <Save className="w-4 h-4" />
          {saving ? "Kaydediliyor..." : "Reklam Ayarlarını Kaydet"}
        </Button>
      </div>

      {/* Applications list */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-[#8A252C]" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Başvurular</div>
              <h3 className="font-display text-xl font-bold text-[#1F2937]">Reklam Başvuruları ({apps.length})</h3>
            </div>
          </div>
          <Button onClick={fetchApps} variant="outline" data-testid="ad-apps-refresh-btn" className="border-stone-300 gap-2">
            <RefreshCw className="w-4 h-4" /> Yenile
          </Button>
        </div>

        <div className="space-y-2">
          {loadingApps ? (
            <p className="text-sm text-[#4B5563] text-center py-6">Yükleniyor...</p>
          ) : apps.length === 0 ? (
            <p className="text-sm text-[#4B5563] text-center py-6">Henüz başvuru yok.</p>
          ) : (
            apps.map((a) => {
              const meta = STATUS_META[a.status] || STATUS_META.pending;
              return (
                <div key={a.id} data-testid={`ad-app-${a.id}`} className="border border-stone-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-start gap-3">
                  {a.logo_url && (
                    <a
                      href={resolveUploadUrl(a.logo_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-stone-200 bg-white"
                      data-testid={`ad-app-logo-${a.id}`}
                      title="Logoyu büyüt"
                    >
                      <img src={resolveUploadUrl(a.logo_url)} alt={a.company_name}
                        className="w-full h-full object-contain" />
                    </a>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`${meta.color} font-semibold`}>{meta.label}</Badge>
                      {a.package_label && (
                        <Badge variant="outline" className="bg-[#D4AF37]/15 text-[#8A252C] border-[#D4AF37]/40 font-semibold">
                          {a.package_label}
                        </Badge>
                      )}
                      <span className="text-xs text-[#4B5563]">
                        {new Date(a.created_at).toLocaleString("tr-TR")}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">
                      <InfoLine icon={UserIcon}>{a.full_name}</InfoLine>
                      <InfoLine icon={Building2}>{a.company_name}</InfoLine>
                      <InfoLine icon={Phone}>
                        <a href={`tel:${a.phone}`} className="text-[#8A252C] font-medium">{a.phone}</a>
                      </InfoLine>
                      {a.website && (
                        <InfoLine icon={Globe}>
                          <a href={a.website.startsWith("http") ? a.website : `https://${a.website}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-[#8A252C] font-medium truncate">{a.website}</a>
                        </InfoLine>
                      )}
                    </div>
                    {a.message && (
                      <div className="mt-2 text-sm text-[#4B5563] bg-stone-50 rounded-lg px-3 py-2">{a.message}</div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {a.status !== "contacted" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(a, "contacted")}
                        data-testid={`ad-app-contact-${a.id}`}
                        className="border-stone-300 gap-1 h-9">
                        <Mail className="w-3.5 h-3.5" /> İletişime Geçildi
                      </Button>
                    )}
                    {a.status !== "approved" && (
                      <Button size="sm" onClick={() => setStatus(a, "approved")}
                        data-testid={`ad-app-approve-${a.id}`}
                        className="bg-[#4A5D23] hover:bg-[#3d4c1c] text-white gap-1 h-9">
                        Onayla
                      </Button>
                    )}
                    {a.status !== "rejected" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(a, "rejected")}
                        data-testid={`ad-app-reject-${a.id}`}
                        className="border-stone-300 text-[#7F1D1D] hover:bg-[#7F1D1D]/5 gap-1 h-9">
                        Reddet
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteApp(a)}
                      data-testid={`ad-app-delete-${a.id}`}
                      className="text-[#7F1D1D] hover:bg-[#7F1D1D]/5 h-9 w-9 p-0" title="Sil">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const InfoLine = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 min-w-0">
    <Icon className="w-3.5 h-3.5 text-[#8A252C] shrink-0" />
    <span className="truncate">{children}</span>
  </div>
);

export default AdManagement;
